import React from "react";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

import Button, { ButtonTypes } from "../../../../components/Button";
import { DonutRing } from "../../../../components/DonutRing";
import { useResponsive } from "../../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../../utils/constants";
import { formatCurrency } from "../../../../utils/numbers";
import { LiabilitiesObject } from "../../types";
import { LiabilitiesHeroProps } from "./types";
import {
  BarSegment,
  BarTrack,
  BarReveal,
  CompactLegend,
  DebtFreeBlock,
  DebtFreeNote,
  DebtFreeYear,
  DebtRemainingValue,
  DonutBlock,
  DonutCaption,
  DonutOverlay,
  DonutPercent,
  HeroCard,
  HeroDivider,
  HeroLabel,
  Legend,
  LegendDivider,
  LegendHeader,
  LegendItem,
  LegendSwatch,
  LegendValue,
  PaidSoFarBand,
  PaidSoFarHeader,
  PaidSoFarTotal,
  PrimaryBlock,
  TopBar,
  WhereItWent,
} from "./styles";

// Payoff donut sits inline on compact layouts but anchors the card on desktop.
const DONUT_SIZE_COMPACT = 112;
const DONUT_SIZE_FULL = 200;

const getLatestEndYear = (liabilities: LiabilitiesObject[]): number | null => {
  const years = liabilities
    .map((liability) => new Date(liability.endDate).getFullYear())
    .filter((year) => !Number.isNaN(year));

  if (years.length === 0) {
    return null;
  }

  return Math.max(...years);
};

export const LiabilitiesHero: React.FC<LiabilitiesHeroProps> = ({
  totals,
  liabilities,
  periodLabel,
  onCreateLiability,
}) => {
  const isCompact = useResponsive(BREAKPOINTS.tabletLarge);

  const {
    currentAmount,
    amountLent,
    principalAmount,
    interestAmount,
    paidAmount,
  } = totals;

  const headerLabel = `Liabilities - ${periodLabel}`;

  const paidOffPercent =
    amountLent > 0 ? Math.round((principalAmount / amountLent) * 100) : 0;
  const donutProgress = paidOffPercent / 100;

  const debtFreeYear = getLatestEndYear(liabilities);
  const hasProjection = debtFreeYear !== null;
  const currentYear = new Date().getFullYear();
  const yearsToGo = hasProjection ? Math.max(0, debtFreeYear - currentYear) : 0;
  const yearLabel = yearsToGo === 1 ? "year" : "years";
  const yearsToGoText = `${yearsToGo} ${yearLabel} to go`;
  const debtFreeYearDisplay = hasProjection ? debtFreeYear.toString() : "—";

  const paidTotal = principalAmount + interestAmount;
  const principalRatio = paidTotal > 0 ? principalAmount / paidTotal : 0;
  const interestRatio = paidTotal > 0 ? interestAmount / paidTotal : 0;

  const paymentBar = (
    <BarTrack>
      <BarReveal>
        <BarSegment $ratio={principalRatio} $tone="principal" />
        <BarSegment $ratio={interestRatio} $tone="interest" />
      </BarReveal>
    </BarTrack>
  );

  const principalLegendItem = (
    <LegendItem>
      <LegendHeader>
        <LegendSwatch $tone="principal" />
        <HeroLabel>Principal</HeroLabel>
      </LegendHeader>
      <LegendValue>{formatCurrency(principalAmount)}</LegendValue>
    </LegendItem>
  );

  const interestLegendItem = (
    <LegendItem>
      <LegendHeader>
        <LegendSwatch $tone="interest" />
        <HeroLabel>Interest</HeroLabel>
      </LegendHeader>
      <LegendValue>{formatCurrency(interestAmount)}</LegendValue>
    </LegendItem>
  );

  const breakdownSection = isCompact ? (
    <PaidSoFarBand>
      <PaidSoFarHeader>
        <HeroLabel>Paid so far</HeroLabel>
        <PaidSoFarTotal>{formatCurrency(paidAmount)}</PaidSoFarTotal>
      </PaidSoFarHeader>
      {paymentBar}
      <CompactLegend>
        {principalLegendItem}
        {interestLegendItem}
      </CompactLegend>
    </PaidSoFarBand>
  ) : (
    <>
      <HeroDivider />
      <WhereItWent>
        <HeroLabel>Where it went</HeroLabel>
        {paymentBar}
        <Legend>
          {principalLegendItem}
          {interestLegendItem}
          <LegendDivider />
          <LegendItem>
            <LegendHeader>
              <HeroLabel>Paid so far</HeroLabel>
            </LegendHeader>
            <LegendValue>{formatCurrency(paidAmount)}</LegendValue>
          </LegendItem>
        </Legend>
      </WhereItWent>
    </>
  );

  return (
    <HeroCard aria-label="Debt summary">
      {!isCompact && (
        <TopBar>
          <HeroLabel>{headerLabel}</HeroLabel>
          <Button
            type={ButtonTypes.primary}
            size="medium"
            onClick={onCreateLiability}
            aria-label="Add liability"
          >
            <AddRoundedIcon fontSize="small" />
            Add liability
          </Button>
        </TopBar>
      )}

      <DonutBlock>
        <DonutRing
          progress={donutProgress}
          color="var(--color-primary)"
          displayValue=""
          size={isCompact ? DONUT_SIZE_COMPACT : DONUT_SIZE_FULL}
        />
        <DonutOverlay>
          <DonutPercent>{paidOffPercent}%</DonutPercent>
          <DonutCaption>Paid off</DonutCaption>
        </DonutOverlay>
      </DonutBlock>

      <PrimaryBlock>
        <HeroLabel>Debt remaining</HeroLabel>
        <DebtRemainingValue>{formatCurrency(currentAmount)}</DebtRemainingValue>
      </PrimaryBlock>

      <DebtFreeBlock>
        <HeroLabel>Debt-free by</HeroLabel>
        <DebtFreeYear>{debtFreeYearDisplay}</DebtFreeYear>
        {hasProjection && <DebtFreeNote>{yearsToGoText}</DebtFreeNote>}
      </DebtFreeBlock>

      {breakdownSection}
    </HeroCard>
  );
};
